import { useCallback, useEffect, useState } from 'react';
import { History, Loader2, RotateCcw, Camera, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { authFetch } from '../lib/authFetch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REASON_LABELS = {
  auto: 'Autosave',
  manual: 'Saved by you',
  pre_restore: 'Before restore',
};

function relativeTime(iso) {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  const mins = Math.round((Date.now() - dt.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`;
  return dt.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function summaryLine(summary) {
  if (!summary) return '';
  const parts = [];
  parts.push(`${summary.characters ?? 0} character${summary.characters === 1 ? '' : 's'}`);
  parts.push(`${summary.scenes ?? 0} scene${summary.scenes === 1 ? '' : 's'}`);
  parts.push(`${summary.world_items ?? 0} world item${summary.world_items === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

/**
 * VersionHistoryModal — lists a project's snapshots (newest first) and
 * restores any of them in one click. Restore always snapshots the CURRENT
 * state first (reason "pre_restore"), so restoring is itself undoable.
 * Used from both the ProjectWorkspace and SeriesBuilder headers.
 */
export default function VersionHistoryModal({ projectId, onClose, onRestored }) {
  const [snapshots, setSnapshots] = useState(null);
  const [confirming, setConfirming] = useState(null); // snapshot pending confirm
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/projects/${projectId}/snapshots`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setSnapshots(data.snapshots || []);
    } catch {
      toast.error('Could not load version history');
      setSnapshots([]);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const snapshotNow = async () => {
    setBusy(true);
    try {
      const res = await authFetch(`${API}/projects/${projectId}/snapshots`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error();
      toast.success('Version saved');
      await load();
    } catch {
      toast.error('Could not save a version');
    } finally {
      setBusy(false);
    }
  };

  const restore = async (snapshot) => {
    setBusy(true);
    try {
      const res = await authFetch(
        `${API}/projects/${projectId}/snapshots/${snapshot.snapshot_id}/restore`,
        { method: 'POST', credentials: 'include' },
      );
      if (!res.ok) {
        // The backend aborts cleanly on 404/409/500 pre-apply; a mid-apply
        // failure can leave partial state, so never claim "nothing changed".
        const detail = res.status >= 400 && res.status < 500
          ? await res.json().then(d => d?.detail).catch(() => null)
          : null;
        throw new Error(
          typeof detail === 'string' && detail
            ? detail.slice(0, 200)
            : 'Restore did not complete. Your previous state was saved to '
              + 'history first — retry, or restore the "Before restore" entry.',
        );
      }
      toast.success('Version restored — reloading');
      onRestored?.();
      setBusy(false);
      setConfirming(null);
    } catch (err) {
      toast.error(err?.message || 'Restore did not complete — check version history');
      setBusy(false);
      setConfirming(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border/40 w-full max-w-lg max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border/40 flex-shrink-0">
          <div className="flex items-center gap-3">
            <History className="w-5 h-5 text-primary" />
            <h2 className="font-mono text-lg font-semibold tracking-tight">Version History</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={snapshotNow}
              disabled={busy}
              className="rounded-none font-mono uppercase tracking-widest text-[10px]"
            >
              <Camera className="w-3 h-3 mr-1" /> Save version now
            </Button>
            <button
              onClick={onClose}
              aria-label="Close"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {snapshots === null && (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          )}

          {snapshots !== null && snapshots.length === 0 && (
            <p className="font-mono text-sm text-muted-foreground text-center py-10">
              No versions yet. A version is saved automatically as you work,
              or save one now.
            </p>
          )}

          {snapshots !== null && snapshots.map((snap, idx) => (
            <div
              key={snap.snapshot_id}
              className="border border-border/40 p-4 mb-2 flex items-center justify-between gap-4"
            >
              <div className="min-w-0">
                <p className="font-mono text-sm text-foreground">
                  {relativeTime(snap.created_at)}
                  <span className="ml-2 text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-border/40 text-muted-foreground">
                    {REASON_LABELS[snap.reason] || snap.reason}
                  </span>
                  {idx === 0 && (
                    <span className="ml-2 text-[10px] uppercase tracking-widest text-primary">
                      newest
                    </span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground mt-1 truncate">
                  {summaryLine(snap.summary)}
                </p>
              </div>

              {confirming === snap.snapshot_id ? (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    onClick={() => restore(snap)}
                    disabled={busy}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-[10px]"
                  >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Confirm restore'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirming(null)}
                    disabled={busy}
                    className="rounded-none font-mono uppercase tracking-widest text-[10px]"
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirming(snap.snapshot_id)}
                  disabled={busy}
                  className="rounded-none font-mono uppercase tracking-widest text-[10px] flex-shrink-0"
                >
                  <RotateCcw className="w-3 h-3 mr-1" /> Restore
                </Button>
              )}
            </div>
          ))}
        </div>

        <p className="px-6 py-3 border-t border-border/40 text-[11px] font-mono text-muted-foreground flex-shrink-0">
          Restoring saves your current state first — a restore can always be undone.
        </p>
      </div>
    </div>
  );
}
